import React, { useState } from 'react';
import { Container, Row, Col, Form, Button, Toast, ToastContainer } from 'react-bootstrap';
import axios from 'axios';
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaGithub, FaLinkedin, FaTwitter, FaGlobe, FaBriefcase, FaHandHoldingHeart, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import './Contact.css';

const DONATION_PRESETS = [100, 500, 1000];

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [status, setStatus] = useState({
    submitted: false,
    submitting: false,
    info: { error: false, msg: null }
  });

  const [toast, setToast] = useState({
    show: false,
    variant: 'success',
    title: '',
    message: ''
  });

  const showToast = (variant, title, message) => {
    setToast({ show: true, variant, title, message });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({
      submitted: false,
      submitting: true,
      info: { error: false, msg: null }
    });

    try {
      await axios.post('/api/contact', formData);

      setStatus({
        submitted: true,
        submitting: false,
        info: { error: false, msg: 'Message sent successfully!' }
      });
      showToast('success', 'Message Sent', "Thanks for reaching out! I'll get back to you soon.");

      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      setStatus({
        submitted: false,
        submitting: false,
        info: { error: true, msg: 'An error occurred. Please try again later.' }
      });
      showToast('danger', 'Message Failed', 'Something went wrong sending your message. Please try again later.');
    }
  };

  // --- Consultation inquiry (free, no payment) ---
  const [consultForm, setConsultForm] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    topic: '',
    preferred_datetime: '',
    message: ''
  });

  const [consultStatus, setConsultStatus] = useState({
    submitted: false,
    submitting: false,
    info: { error: false, msg: null }
  });

  const handleConsultChange = (e) => {
    const { name, value } = e.target;
    setConsultForm((prevData) => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleConsultSubmit = async (e) => {
    e.preventDefault();
    setConsultStatus({
      submitted: false,
      submitting: true,
      info: { error: false, msg: null }
    });

    try {
      await axios.post('/api/consultation', consultForm);

      setConsultStatus({
        submitted: true,
        submitting: false,
        info: { error: false, msg: "Your consultation request has been sent! I'll get back to you soon." }
      });
      showToast('success', 'Request Sent', "Your consultation request has been sent! I'll get back to you soon.");

      setConsultForm({
        name: '',
        email: '',
        company: '',
        phone: '',
        topic: '',
        preferred_datetime: '',
        message: ''
      });
    } catch (error) {
      setConsultStatus({
        submitted: false,
        submitting: false,
        info: { error: true, msg: 'An error occurred. Please try again later.' }
      });
      showToast('danger', 'Request Failed', 'Something went wrong sending your request. Please try again later.');
    }
  };

  // --- Voluntary contribution / donation (Razorpay, test mode) ---
  const [donationForm, setDonationForm] = useState({
    amount_inr: '',
    name: '',
    email: '',
    message: ''
  });

  const [donationStatus, setDonationStatus] = useState({
    submitted: false,
    submitting: false,
    info: { error: false, msg: null }
  });

  const handleDonationChange = (e) => {
    const { name, value } = e.target;
    setDonationForm((prevData) => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleAmountPreset = (amount) => {
    setDonationForm((prevData) => ({
      ...prevData,
      amount_inr: String(amount)
    }));
  };

  const handleDonationSubmit = async (e) => {
    e.preventDefault();
    setDonationStatus({
      submitted: false,
      submitting: true,
      info: { error: false, msg: null }
    });

    if (typeof window.Razorpay === 'undefined') {
      setDonationStatus({
        submitted: false,
        submitting: false,
        info: { error: true, msg: 'Payment gateway failed to load. Please refresh the page and try again.' }
      });
      return;
    }

    try {
      const { data } = await axios.post('/api/payments/donation/create-order', {
        amount_inr: parseFloat(donationForm.amount_inr),
        name: donationForm.name || undefined,
        email: donationForm.email || undefined,
        message: donationForm.message || undefined
      });

      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        order_id: data.order_id,
        name: 'Rahul Bhoyar',
        description: 'Voluntary Contribution',
        prefill: {
          name: donationForm.name,
          email: donationForm.email
        },
        handler: async (response) => {
          try {
            await axios.post('/api/payments/verify', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              reference_id: data.reference_id
            });

            setDonationStatus({
              submitted: true,
              submitting: false,
              info: { error: false, msg: 'Thank you for your contribution!' }
            });
            showToast('success', 'Payment Successful', 'Thank you for your contribution — it truly means a lot!');

            setDonationForm({ amount_inr: '', name: '', email: '', message: '' });
          } catch (verifyError) {
            setDonationStatus({
              submitted: false,
              submitting: false,
              info: { error: true, msg: 'Payment verification failed. Please contact me directly if the amount was deducted.' }
            });
            showToast('danger', 'Verification Failed', 'We could not verify your payment. Please contact me directly if the amount was deducted.');
          }
        },
        modal: {
          ondismiss: () => {
            setDonationStatus({
              submitted: false,
              submitting: false,
              info: { error: false, msg: null }
            });
          }
        }
      });

      rzp.open();
    } catch (error) {
      setDonationStatus({
        submitted: false,
        submitting: false,
        info: { error: true, msg: 'An error occurred. Please try again later.' }
      });
      showToast('danger', 'Payment Failed', 'Something went wrong starting the payment. Please try again later.');
    }
  };

  return (
    <section className="contact-section">
      <ToastContainer position="top-center" className="app-toast-container p-3">
        <Toast
          show={toast.show}
          onClose={() => setToast((prev) => ({ ...prev, show: false }))}
          delay={5000}
          autohide
          className={`app-toast app-toast-${toast.variant}`}
        >
          <Toast.Body className="d-flex align-items-start">
            <span className="app-toast-icon">
              {toast.variant === 'success' ? <FaCheckCircle /> : <FaTimesCircle />}
            </span>
            <span className="app-toast-text">
              <strong className="app-toast-title d-block">{toast.title}</strong>
              {toast.message}
            </span>
            <button
              type="button"
              className="app-toast-close"
              aria-label="Close"
              onClick={() => setToast((prev) => ({ ...prev, show: false }))}
            >
              &times;
            </button>
          </Toast.Body>
        </Toast>
      </ToastContainer>
      <Container>
        <h2 className="section-title text-center">Contact Me</h2>
        <div className="section-intro text-center mb-5">
          <p className="lead">
            Let's discuss how we can work together on your next project
          </p>
        </div>
        <Row>
          <Col lg={5} className="mb-4 mb-lg-0">
            <div className="contact-card contact-info-card">
              <div className="card-body text-left">
                <h3 className="h5 mb-4">Get In Touch</h3>

                <div className="contact-item">
                  <div className="contact-icon-wrapper">
                    <FaEnvelope className="contact-icon" />
                  </div>
                  <div>
                    <h4 className="contact-info-title">Email</h4>
                    <p className="contact-info-text">rahulbhoyaroffice@gmail.com</p>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-icon-wrapper">
                    <FaPhone className="contact-icon" />
                  </div>
                  <div>
                    <h4 className="contact-info-title">Phone</h4>
                    <p className="contact-info-text">+49 176 31676744</p>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-icon-wrapper">
                    <FaGlobe className="contact-icon" />
                  </div>
                  <div>
                    <h4 className="contact-info-title">Website</h4>
                    <p className="contact-info-text">
                      <a href="http://rahulbhoyar.com" target="_blank" rel="noopener noreferrer">rahulbhoyar.com</a>
                    </p>
                  </div>
                </div>

                <div className="contact-item">
                  <div className="contact-icon-wrapper">
                    <FaMapMarkerAlt className="contact-icon" />
                  </div>
                  <div>
                    <h4 className="contact-info-title">Location</h4>
                    <p className="contact-info-text">Frankfurt, Germany</p>
                  </div>
                </div>

                <div className="contact-map-wrapper">
                  <iframe
                    title="Location map - Frankfurt, Germany"
                    src="https://www.google.com/maps?q=Frankfurt,+Germany&output=embed"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    allowFullScreen
                  />
                </div>

                <div className="contact-social">
                  <a href="https://github.com/rahulbhoyar1995" className="contact-social-link github" target="_blank" rel="noopener noreferrer">
                    <FaGithub />
                  </a>
                  <a href="https://www.linkedin.com/in/rahul-bhoyar-mba-1a04a7215/" className="contact-social-link linkedin" target="_blank" rel="noopener noreferrer">
                    <FaLinkedin />
                  </a>
                </div>
              </div>
            </div>
          </Col>

          <Col lg={7}>
            <div className="contact-card contact-form-card">
              <div className="card-body text-left">
                <Form onSubmit={handleSubmit} className="contact-form">
                  <Row>
                    <Col md={6} className="mb-3">
                      <Form.Group controlId="name">
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Your name"
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                      <Form.Group controlId="email">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="Your email address"
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Form.Group className="mb-3" controlId="subject">
                    <Form.Label>Subject</Form.Label>
                    <Form.Control
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      placeholder="Message subject"
                      required
                    />
                  </Form.Group>
                  <Form.Group className="mb-4" controlId="message">
                    <Form.Label>Message</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={5}
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Your message"
                      required
                    />
                  </Form.Group>
                  <Button
                    type="submit"
                    disabled={status.submitting}
                    className="contact-submit-btn w-100"
                  >
                    {status.submitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </Form>
              </div>
            </div>
          </Col>
        </Row>

        <Row className="mt-5">
          <Col lg={6} className="mb-4 mb-lg-0">
            <div className="contact-card consultation-card">
              <div className="card-body text-left">
                <h3 className="h5 mb-2">
                  <FaBriefcase className="me-2" />
                  Business Consultation
                </h3>
                <p className="contact-info-text mb-4">
                  Looking to work together on a business or technical project? Send me the details and I'll get back to you — no charge for the inquiry.
                </p>

                <Form onSubmit={handleConsultSubmit} className="contact-form">
                  <Row>
                    <Col md={6} className="mb-3">
                      <Form.Group controlId="consult-name">
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                          type="text"
                          name="name"
                          value={consultForm.name}
                          onChange={handleConsultChange}
                          placeholder="Your name"
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                      <Form.Group controlId="consult-email">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={consultForm.email}
                          onChange={handleConsultChange}
                          placeholder="Your email address"
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6} className="mb-3">
                      <Form.Group controlId="consult-company">
                        <Form.Label>Company (optional)</Form.Label>
                        <Form.Control
                          type="text"
                          name="company"
                          value={consultForm.company}
                          onChange={handleConsultChange}
                          placeholder="Your company"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                      <Form.Group controlId="consult-phone">
                        <Form.Label>Phone (optional)</Form.Label>
                        <Form.Control
                          type="tel"
                          name="phone"
                          value={consultForm.phone}
                          onChange={handleConsultChange}
                          placeholder="Your phone number"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Form.Group className="mb-3" controlId="consult-topic">
                    <Form.Label>Topic</Form.Label>
                    <Form.Control
                      type="text"
                      name="topic"
                      value={consultForm.topic}
                      onChange={handleConsultChange}
                      placeholder="What would you like to discuss?"
                      required
                    />
                  </Form.Group>
                  <Form.Group className="mb-3" controlId="consult-preferred-datetime">
                    <Form.Label>Preferred Date/Time (optional)</Form.Label>
                    <Form.Control
                      type="text"
                      name="preferred_datetime"
                      value={consultForm.preferred_datetime}
                      onChange={handleConsultChange}
                      placeholder="e.g. Weekdays after 6 PM CET"
                    />
                  </Form.Group>
                  <Form.Group className="mb-4" controlId="consult-message">
                    <Form.Label>Additional Details (optional)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      name="message"
                      value={consultForm.message}
                      onChange={handleConsultChange}
                      placeholder="Tell me more about your project or business needs"
                    />
                  </Form.Group>
                  <Button
                    type="submit"
                    disabled={consultStatus.submitting}
                    className="contact-submit-btn w-100"
                  >
                    {consultStatus.submitting ? 'Sending...' : 'Request Consultation'}
                  </Button>
                </Form>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className="contact-card donation-card">
              <div className="card-body text-left">
                <h3 className="h5 mb-2">
                  <FaHandHoldingHeart className="me-2" />
                  Support My Work
                </h3>
                <p className="contact-info-text mb-4">
                  If my work has helped you, consider a voluntary contribution. Every bit is appreciated!
                </p>

                <Form onSubmit={handleDonationSubmit} className="contact-form">
                  <Form.Group className="mb-3">
                    <Form.Label>Amount (INR)</Form.Label>
                    <div className="donation-amount-presets mb-2">
                      {DONATION_PRESETS.map((amount) => (
                        <button
                          type="button"
                          key={amount}
                          className={`donation-amount-btn${String(amount) === donationForm.amount_inr ? ' active' : ''}`}
                          onClick={() => handleAmountPreset(amount)}
                        >
                          ₹{amount}
                        </button>
                      ))}
                    </div>
                    <Form.Control
                      type="number"
                      name="amount_inr"
                      value={donationForm.amount_inr}
                      onChange={handleDonationChange}
                      placeholder="Enter a custom amount"
                      min="10"
                      step="1"
                      required
                    />
                  </Form.Group>
                  <Row>
                    <Col md={6} className="mb-3">
                      <Form.Group controlId="donation-name">
                        <Form.Label>Name (optional)</Form.Label>
                        <Form.Control
                          type="text"
                          name="name"
                          value={donationForm.name}
                          onChange={handleDonationChange}
                          placeholder="Your name"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                      <Form.Group controlId="donation-email">
                        <Form.Label>Email (optional)</Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={donationForm.email}
                          onChange={handleDonationChange}
                          placeholder="Your email address"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Form.Group className="mb-4" controlId="donation-message">
                    <Form.Label>Message (optional)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="message"
                      value={donationForm.message}
                      onChange={handleDonationChange}
                      placeholder="Leave a message"
                    />
                  </Form.Group>
                  <Button
                    type="submit"
                    disabled={donationStatus.submitting}
                    className="contact-submit-btn w-100"
                  >
                    {donationStatus.submitting ? 'Processing...' : 'Contribute'}
                  </Button>
                </Form>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default Contact;
